import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, CreditCard, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

interface Payment {
  id: number;
  person_id: number;
  agency_id: number;
  amount: number;
  payment_type: string | null;
  date: string | null;
  notes: string | null;
  created_by: number | null;
  author_first_name?: string;
  author_last_name?: string;
}

interface PaymentsListProps {
  personId: string;
}

export function PaymentsList({ personId }: PaymentsListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);

  const { data, isLoading } = useQuery<{ data: Payment[] }>({
    queryKey: ["payments", personId],
    queryFn: () => api.get<{ data: Payment[] }>(`/clients/${personId}/payments`),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (body: { amount: number; paymentType?: string; date?: string; notes?: string }) =>
      api.post(`/clients/${personId}/payments`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments", personId] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number; amount?: number; paymentType?: string; date?: string; notes?: string }) =>
      api.put(`/clients/${personId}/payments/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments", personId] });
      setEditingPayment(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/clients/${personId}/payments/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["payments", personId] }),
  });

  const payments = data?.data ?? [];
  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Payments</h3>
          <p className="text-sm text-muted-foreground">Total: ${totalPayments.toFixed(2)}</p>
        </div>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Payment
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <PaymentForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingPayment && (
        <Card>
          <CardContent className="p-4">
            <PaymentForm
              initial={editingPayment}
              onSubmit={(d) => updateMutation.mutate({ id: editingPayment.id, ...d })}
              onCancel={() => setEditingPayment(null)}
              isLoading={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && <div className="h-20 animate-pulse rounded-lg border bg-muted" />}

      {!isLoading && payments.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <CreditCard className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No payments recorded.</p>
          </CardContent>
        </Card>
      )}

      {payments.map((p) => {
        const authorName = p.author_first_name && p.author_last_name
          ? `${p.author_first_name} ${p.author_last_name}` : null;

        return (
          <Card key={p.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium">
                    ${p.amount.toFixed(2)} {p.payment_type && `- ${p.payment_type}`}
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    {p.date ? new Date(p.date).toLocaleDateString() : "No date"}
                    {authorName && ` | By: ${authorName}`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setEditingPayment(p)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => {
                    if (confirm("Delete this payment?")) deleteMutation.mutate(p.id);
                  }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            {p.notes && (
              <CardContent>
                <p className="text-xs text-muted-foreground">{p.notes}</p>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

interface PaymentFormProps {
  initial?: Payment;
  onSubmit: (data: { amount: number; paymentType?: string; date?: string; notes?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function PaymentForm({ initial, onSubmit, onCancel, isLoading }: PaymentFormProps) {
  const [amount, setAmount] = useState(initial ? String(initial.amount) : "");
  const [paymentType, setPaymentType] = useState(initial?.payment_type ?? "");
  const [date, setDate] = useState(initial?.date ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Input label="Amount ($)" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Input label="Payment Type" value={paymentType} onChange={(e) => setPaymentType(e.target.value)} placeholder="Check, Cash, etc." />
        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes..." />
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => onSubmit({
            amount: parseFloat(amount) || 0,
            paymentType: paymentType || undefined,
            date: date || undefined,
            notes: notes || undefined,
          })}
          loading={isLoading}
          disabled={!amount}
        >
          {initial ? "Update Payment" : "Add Payment"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
